import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ContentPage } from '../../_core/state/content-pages/content-page.model';
import { ContentSectionQuery } from '../../_core/state/content-section/content-section.query';
import { SeoService } from '../../_core/services/seo.service';
import { GlobalQuery, GlobalService } from '../../_core/state/global';
import { SessionQuery } from '../../_core/state/session';
import { ProjectsService } from '../../_core/state/project';
import { ProjectActionsService } from '../projects/services/project-actions.service';
import { PublicCreativeUnitPackageListing } from '../../../../../api/src/creative-unit-package-listing/creative-unit-package-listing.entity';

@Component({
  selector: 'app-dynamic-content-page',
  templateUrl: './dynamic-content-page.component.html',
  styleUrls: ['./dynamic-content-page.component.scss']
})
export class DynamicContentPageComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  page: ContentPage | null = null;
  loading = false;
  error: string | null = null;
  contentSections$ = this.contentSectionQuery.selectAll();
  contextState: any = {};
  private creatingProject = false;

  constructor(
    private route: ActivatedRoute,
    private seoService: SeoService,
    private contentSectionQuery: ContentSectionQuery,
    private globalService: GlobalService,
    private readonly globalQuery: GlobalQuery,
    public sessionQuery: SessionQuery,
    private readonly projectsService: ProjectsService,
    private readonly router: Router,
    private readonly projectActionsService: ProjectActionsService
  ) {
    // Set up context state for content sections
    this.contextState.locale = this.globalService.getLocale();
    this.contextState.user = {
      ...(this.sessionQuery.getValue() || {}),
      isAtLeastAdmin: this.sessionQuery.isAtLeastAdmin()
    };
  }

  ngOnInit() {
    // Get the resolved page from route data
    this.route.data
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.page = data['page'] as ContentPage;
        
        if (this.page) {
          // Update SEO tags
          this.updateSeoTags();
          // Set loading to false since we have the page
          this.loading = false;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clear SEO tags when leaving the page
    this.seoService.clearMetaTags();
  }

  private updateSeoTags() {
    if (!this.page) {
      return;
    }

    // Get current URL for OG tags
    const currentUrl = window.location.href;

    // Update all SEO tags
    this.seoService.updatePageSeo({
      title: this.page.title,
      metaTitle: this.page.metaTitle,
      metaDescription: this.page.metaDescription,
      url: currentUrl
    });
  }

  get hasContent(): boolean {
    return this.contentSectionQuery.getCount() > 0;
  }

  get pageTitle(): string {
    return this.page?.title || 'Page';
  }

  get pageSlug(): string {
    return this.page?.slug || '';
  }

  reload() {
    // Reload the page by re-navigating to the same route
    window.location.reload();
  }

  handleTemplateSelection(listing: PublicCreativeUnitPackageListing | null) {
    if (!listing || this.creatingProject) {
      return;
    }

    const businessId = this.getDefaultBusinessId();

    if (!businessId) {
      this.globalService.triggerCustomMessage($localize`:Project Creation Error@@dynamicContentPageProjectCreationError:Unable to create a project. Please ensure you have an active business.`);
      return;
    }

    this.creatingProject = true;
    this.projectActionsService.projectCreating.next(true);

    this.projectsService
      .add(
        businessId,
        $localize`:Default Project Name@@dynamicContentPageDefaultProjectName:New project`,
        this.getDefaultProjectVersion()
      )
      .pipe(
        finalize(() => {
          this.creatingProject = false;
          this.projectActionsService.projectCreating.next(false);
        })
      )
      .subscribe({
        next: project => {
          const queryParams = this.buildTemplateNavigationParams(listing);
          this.router.navigate(['/project', project.id, 'ad-studio'], {
            queryParams
          });
        },
        error: err => {
          this.globalService.triggerErrorMessage(err);
        }
      });
  }

  private getDefaultBusinessId(): string | null {
    const session = this.sessionQuery.getValue();
    const profileBusiness = session?.profile?.businesses?.[0]?.id;
    if (profileBusiness) {
      return profileBusiness;
    }

    const businessUser = session?.businessUser?.[0]?.businessesId;
    return businessUser || null;
  }

  private buildTemplateNavigationParams(listing: PublicCreativeUnitPackageListing): Params {
    const params: Params = {};

    if (listing?.id && listing?.creativeUnitPackageId) {
      params.listingId = listing.id;
    }

    const packageId =
      listing?.creativeUnitPackageId ||
      listing?.creativeUnitPackage?.id ||
      listing?.creativeUnitPackageSummary?.id ||
      listing?.id;
    if (packageId) {
      params.packageId = packageId;
    }

    return params;
  }

  private getDefaultProjectVersion(): '1' | '2' {
    const settings = this.globalQuery.getValue()?.settings;
    return settings?.disableVersion1Projects ? '2' : '1';
  }
}
